import { assertPartialSchema, createAction, useSelector, LOGOUT, v } from "../../../lib";
import { AUTH_REFRESHING_FAILURE } from "../auth";

export enum ConnectivityActionType {
  CONNECTIVITY_SERVICE_UNAVAILABLE_SET = "CONNECTIVITY_SERVICE_UNAVAILABLE_SET",
  CONNECTIVITY_FAILED_DURING_OUTAGE_SET = "CONNECTIVITY_FAILED_DURING_OUTAGE_SET",
}

export interface ConnectivityState {
  // when the backend first looked unreachable, or null once it answers again
  serviceUnavailableAt: number | null;
  // a request that was unanswered during a reconnecting outage failed because of it; cleared when
  // the user dismisses the banner's reload offer or the session ends
  failedDuringOutage: boolean;
}

export const doCONNECTIVITY_SERVICE_UNAVAILABLE_SET = createAction<{
  serviceUnavailableAt: number | null;
}>(ConnectivityActionType.CONNECTIVITY_SERVICE_UNAVAILABLE_SET);

export const doCONNECTIVITY_FAILED_DURING_OUTAGE_SET = createAction<{
  failedDuringOutage: boolean;
}>(ConnectivityActionType.CONNECTIVITY_FAILED_DURING_OUTAGE_SET);

export const connectivityActionCreators = {
  doCONNECTIVITY_SERVICE_UNAVAILABLE_SET,
  doCONNECTIVITY_FAILED_DURING_OUTAGE_SET,
};

interface RootState {
  connectivity: ConnectivityState;
}

export const useSelectConnectivityState = () => {
  return useSelector((state: RootState) => state.connectivity);
};

// deliberately never persisted: a reload should re-discover the backend, not remember an outage
export const connectivityDefaultState: ConnectivityState = {
  serviceUnavailableAt: null,
  failedDuringOutage: false,
};

type Action =
  | {
      type: ConnectivityActionType.CONNECTIVITY_SERVICE_UNAVAILABLE_SET;
      payload: { serviceUnavailableAt: number | null };
    }
  | {
      type: ConnectivityActionType.CONNECTIVITY_FAILED_DURING_OUTAGE_SET;
      payload: { failedDuringOutage: boolean };
    }
  | {
      type: typeof AUTH_REFRESHING_FAILURE | typeof LOGOUT;
      payload?: undefined;
    };

export const connectivityReducer = (
  state: ConnectivityState = { ...connectivityDefaultState },
  { type, payload }: Action,
): ConnectivityState => {
  switch (type) {
    case ConnectivityActionType.CONNECTIVITY_SERVICE_UNAVAILABLE_SET:
      assertPartialSchema({
        payload,
        schema: v.object({
          serviceUnavailableAt: v.number().allow(null).exist(),
        }),
      });

      return { ...state, serviceUnavailableAt: payload.serviceUnavailableAt };

    case ConnectivityActionType.CONNECTIVITY_FAILED_DURING_OUTAGE_SET:
      assertPartialSchema({
        payload,
        schema: v.object({
          failedDuringOutage: v.boolean().exist(),
        }),
      });

      if (state.failedDuringOutage === payload.failedDuringOutage) return state;
      return { ...state, failedDuringOutage: payload.failedDuringOutage };

    // the session ended instead of recovering, so there is no page left to reload into
    case AUTH_REFRESHING_FAILURE:
    case LOGOUT:
      return state.failedDuringOutage ? { ...state, failedDuringOutage: false } : state;

    default:
      return state;
  }
};
